import { Box, Typography } from '@mui/material';

/**
 * Animated dollar sign spinner - the signature Finstacks loading indicator
 * Replaces all CircularProgress spinners throughout the app
 */

// Shared keyframes for the dollar spin animation
const dollarSpinKeyframes = {
  '@keyframes dollarSpin': {
    '0%, 100%': { 
      transform: 'rotateY(0deg) scale(1)',
      opacity: 0.4
    },
    '50%': { 
      transform: 'rotateY(180deg) scale(1.2)',
      opacity: 1
    }
  }
};

/**
 * Standard dollar spinner - use for most loading states
 * @param {string} size - 'small' (16px), 'medium' (24px), 'large' (40px), or custom px value
 * @param {string} message - Optional loading message to display below
 * @param {boolean} inline - If true, displays horizontally with message
 */
export const DollarSpinner = ({ size = 'medium', message, inline = false }) => {
  const sizeMap = {
    small: '1rem',      // 16px - for buttons, inline indicators
    medium: '1.5rem',   // 24px - standard loading
    large: '2.5rem',    // 40px - full page loading
  };
  
  const fontSize = sizeMap[size] || size;
  
  const spinnerBox = (
    <Box 
      sx={{ 
        display: 'flex', 
        gap: 0.5,
        '& > span': {
          fontSize,
          color: 'success.main',
          animation: 'dollarSpin 1.2s ease-in-out infinite',
        },
        '& > span:nth-of-type(1)': { animationDelay: '0s' },
        '& > span:nth-of-type(2)': { animationDelay: '0.2s' },
        '& > span:nth-of-type(3)': { animationDelay: '0.4s' },
        ...dollarSpinKeyframes
      }}
    >
      <span>$</span>
      <span>$</span>
      <span>$</span>
    </Box>
  );

  if (!message) {
    return spinnerBox;
  }

  if (inline) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        {spinnerBox}
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
      {spinnerBox}
      <Typography variant="body2" color="textSecondary">
        {message}
      </Typography>
    </Box>
  );
};

/**
 * Centered loader - for page/card loading states
 */
export const CenteredDollarLoader = ({ message = "Loading...", size = 'large', minHeight = '200px' }) => {
  return (
    <Box 
      display="flex" 
      flexDirection="column"
      justifyContent="center" 
      alignItems="center" 
      minHeight={minHeight}
      gap={2}
    >
      <DollarSpinner size={size} />
      {message && (
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Inline loader - for buttons and small spaces
 */
export const InlineDollarLoader = ({ message, size = 'small' }) => {
  return <DollarSpinner size={size} message={message} inline />;
};

/**
 * Overlay loader - for full-screen loading states
 */
export const OverlayDollarLoader = ({ message = "Loading...", backdrop = true }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: backdrop ? 'rgba(10, 11, 13, 0.5)' : 'transparent', // §2.8: scrim tracks near-black
        zIndex: 1300,
        gap: 2
      }}
    >
      <DollarSpinner size="large" />
      <Typography variant="h6" color={backdrop ? '#fbfbfc' : 'textPrimary'}>
        {message}
      </Typography>
    </Box>
  );
};

/**
 * Card loader overlay
 */
export const CardDollarLoader = ({ loading, children, message = "Loading..." }) => {
  return (
    <Box position="relative">
      {children}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(251, 251, 252, 0.8)', // §2.8: wash tracks off-white
            borderRadius: 1,
            gap: 1
          }}
        >
          <DollarSpinner size="medium" />
          <Typography variant="body2" color="textSecondary">
            {message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DollarSpinner;
